import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function UniversalLifeInsurance() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="universal-life-insurance" />
          {/* Header */}
          <div className="text-center mb-12 pt-4"> {/* Added padding top */}
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Universal Life Insurance</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Flexible Permanent Protection with Tax-Advantaged Investment Growth
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is Universal Life Insurance? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is Universal Life (UL) Insurance?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                Universal Life (UL) insurance is a type of permanent life insurance known for its flexibility. It combines lifelong death benefit protection with a savings or investment component (cash value) that grows tax-deferred.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                Unlike Whole Life with its fixed premiums, UL policies allow policyholders to adjust their premium payments (within certain limits) and potentially modify the death benefit amount over time. The cash value growth depends on the performance of investment options chosen by the policyholder within the policy.
              </Text>
            </Card>

            {/* How It Works */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How It Works</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Policy Structure:</strong> A UL policy has two main components: the cost of insurance (covering the death benefit) and the investment/savings component (cash value).
                </li>
                <li>
                  <strong>Flexible Premiums:</strong> You can typically pay premiums within a range – a minimum premium to cover the insurance cost and policy fees, and a maximum premium allowed under tax rules. Paying more than the minimum builds cash value faster.
                </li>
                <li>
                  <strong>Investment Options:</strong> You select how the cash value portion is invested from options offered by the insurer (e.g., guaranteed interest accounts, index-linked accounts, managed portfolios similar to mutual funds). Investment performance directly impacts cash value growth.
                </li>
                <li>
                  <strong>Tax-Deferred Growth:</strong> Investment earnings within the policy accumulate tax-deferred.
                </li>
                <li>
                  <strong>Adjustable Death Benefit:</strong> Depending on the policy design, you may be able to increase (subject to underwriting) or decrease the death benefit amount.
                </li>
                 <li>
                  <strong>Accessing Cash Value:</strong> Similar to Whole Life, cash value can typically be accessed via policy loans or withdrawals (subject to policy terms, potential taxes, and impact on death benefit).
                 </li>
                 <li>
                  <strong>Death Benefit Payout:</strong> Upon death, the beneficiaries receive the death benefit. Depending on the policy option chosen at issue, this could be the face amount OR the face amount plus the accumulated cash value. Loans reduce the payout.
                 </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Premium Flexibility:</strong> Ability to adjust payments based on changing financial circumstances (within limits).</li>
                <li><strong>Investment Choice & Potential Growth:</strong> Opportunity for higher cash value growth based on market performance (also carries risk).</li>
                <li><strong>Tax-Advantaged Investing:</strong> Shelters investment growth from annual taxation.</li>
                <li><strong>Adjustable Coverage:</strong> Potential to change the death benefit amount as needs evolve.</li>
                <li><strong>Lifelong Protection:</strong> Provides permanent insurance coverage.</li>
                <li><strong>Estate Planning & Wealth Transfer:</strong> Can be used effectively for estate liquidity, legacy planning, or as a corporate asset.</li>
              </ul>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Considerations</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Important factors to evaluate:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Investment Risk:</strong> Cash value growth is often not guaranteed and depends on the performance of chosen investments. Poor performance can require higher premiums later to maintain coverage.</li>
                <li><strong>Complexity:</strong> Requires understanding investment options, cost structures, and policy management.</li>
                <li><strong>Fees & Charges:</strong> UL policies have various fees (cost of insurance, administration fees, investment management fees) that impact net returns.</li>
                <li><strong>Requires Monitoring:</strong> Policy performance should be reviewed regularly to ensure it aligns with goals and remains adequately funded.</li>
                <li><strong>Potential for Lapse:</strong> If minimum premiums aren't paid or investment performance is poor, the policy could lapse if the cash value is insufficient to cover costs.</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is Universal Life Insurance Right for You?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Universal Life insurance is often suitable for:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Individuals seeking permanent insurance with payment flexibility.</li>
                <li>Those comfortable with investment risk in exchange for potentially higher cash value growth.</li>
                <li>People looking for a tax-sheltered vehicle for long-term investment growth.</li>
                <li>High-income earners or business owners who have maximized other registered savings plans (RRSP, TFSA).</li>
                <li>Individuals planning for significant estate needs or wealth transfer goals.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Combine Flexibility with Permanent Protection</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Explore the flexibility and growth potential of Universal Life insurance. Let's discuss if its features align with your long-term financial objectives.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Discuss Universal Life Options
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
        <BookingWidget /> // Removed onClose prop
      )}
    </MarketingLayout>
  );
}
